import { LoadingButton } from '@mui/lab'
import { Alert, Button, Grid, TextField } from '@mui/material'
import React from 'react'
import { useBoolean } from 'react-use'

import { formatCode } from 'components/common/CodeBlock'
import useAuth from 'hooks/use-auth'
import { trans } from 'i18n/translate'
import { testTeamExternalIntegrationEndpointConfiguration } from 'utils/team-external-integrations/test-endpoint-configuration'

import type {
  EndpointConfiguration,
  EndpointResponse,
  TeamExternalIntegration
} from 'types/api/status-checks/status-check'

const TestTeamExternalIntegration: React.FC<{
  authenticate: boolean
  buttonText?: string
  disabled?: boolean
  endpointConfiguration?: EndpointConfiguration
  integration: TeamExternalIntegration
  statusCheckId?: string
  onError?: () => void
  onResponseReceived?: (endpointResponse: EndpointResponse) => void
}> = ({
  authenticate,
  buttonText,
  disabled,
  endpointConfiguration,
  integration,
  statusCheckId,
  onError,
  onResponseReceived
}) => {
  const { currentTeamId } = useAuth()
  const [isLoading, setIsLoading] = useBoolean(false)
  const [responseBody, setResponseBody] = React.useState<string | null>()
  const [responseCode, setResponseCode] = React.useState<number | null>()
  const [alertSeverity, setAlertSeverity] = React.useState<
    'error' | 'success' | null
  >()
  const shouldDisplayResponseDetail =
    !isLoading && (alertSeverity === 'error' || !authenticate)

  React.useEffect(() => {
    setAlertSeverity(null)
    setResponseBody(null)
    setResponseCode(null)
  }, [integration?.name, endpointConfiguration])

  const abortControllerRef = React.useRef<AbortController>()

  const handleTestClick = async () => {
    setIsLoading(true)

    await testTeamExternalIntegrationEndpointConfiguration({
      abortControllerRef,
      authenticate,
      endpointConfiguration,
      integration,
      statusCheckId,
      teamId: currentTeamId,
      onSuccess: (response) => {
        setIsLoading(false)
        setAlertSeverity('success')
        setResponseBody(response?.responseBody)
        setResponseCode(response?.httpStatusCode)
        onResponseReceived?.(response)
      },
      onError: (body, code) => {
        setIsLoading(false)
        setAlertSeverity('error')
        setResponseBody(body)
        setResponseCode(code)
        onError?.()
      }
    })
  }

  const handleAbortRequest = async () => {
    abortControllerRef.current?.abort()
    setIsLoading(false)
    setAlertSeverity(null)
    setResponseBody('Canceled')
  }

  return (
    <Grid
      container
      data-test-test-status-check-integration
      direction="column"
      rowSpacing={2}
    >
      <Grid item>
        <LoadingButton
          color="primary"
          data-testid={
            authenticate
              ? 'team-integration-auth-test-auth'
              : 'team-integration-auth-test-request'
          }
          disabled={!integration.url || isLoading || disabled}
          loading={isLoading}
          size="small"
          variant="contained"
          onClick={handleTestClick}
        >
          {buttonText ?? `Test ${authenticate ? 'Authentication' : 'Request'}`}
        </LoadingButton>
        {!authenticate && isLoading && (
          <Button
            color="secondary"
            data-testid="cancel-test-status-check"
            disabled={!isLoading}
            size="small"
            sx={{ ml: 1 }}
            variant="contained"
            onClick={handleAbortRequest}
          >
            Cancel Request
          </Button>
        )}
      </Grid>
      {!isLoading && alertSeverity && (
        <Grid item>
          <Alert
            data-testid="test-status-check-integration-alert"
            severity={alertSeverity}
          >
            {alertSeverity === 'error'
              ? trans('status_checks.test_status_check_error')
              : 'Authenticated'}
          </Alert>
        </Grid>
      )}
      {shouldDisplayResponseDetail && responseCode && (
        <Grid item>
          <TextField
            contentEditable={false}
            data-testid="test-status-check-integration-response-code"
            disabled={true}
            fullWidth={true}
            InputProps={{ sx: { fontFamily: 'Monaco' } }}
            label="Response Code"
            sx={{ fontFamily: 'Sohne Mono' }}
            value={responseCode}
          />
        </Grid>
      )}
      {shouldDisplayResponseDetail && responseBody && (
        <Grid item>
          <TextField
            multiline
            contentEditable={false}
            data-testid="test-status-check-integration-response-body"
            disabled={true}
            fullWidth={true}
            InputProps={{ sx: { fontFamily: 'Monaco' } }}
            label="Response Body"
            maxRows={8}
            sx={{ fontFamily: 'Sohne Mono' }}
            value={formatCode(null, responseBody)}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default TestTeamExternalIntegration
